/**
 * M27 task-07 — per-purpose model assignment (ADM-03/04; HUB-082). One
 * dropdown per hub purpose, reading GET /llm-assignments and writing each
 * change through PUT /llm-assignments/:purpose. The purpose list is rendered
 * from the shared `LLM_PURPOSES` single-home — there is NO second copy here
 * (§3.1); a drift against the 11 purposes fails type-check.
 */
import { useEffect, useState } from 'react';
import {
  LLM_PURPOSES,
  type LlmAssignmentView,
  type LlmModel,
  type LlmPurpose,
} from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { PageHeader } from '@/components/PageHeader';
import { hub } from '@/lib/hub-client';

/** The 11 purposes, straight from the shared single-home (no hand-copied list). */
export const assignmentPurposes: LlmPurpose[] = Object.values(LLM_PURPOSES);

export interface AssignmentTableProps {
  models: LlmModel[];
  /** purpose → bound model id (or null when unassigned). */
  assignments: Record<string, string | null>;
  onAssign: (purpose: LlmPurpose, modelId: string) => void;
  busyPurpose: LlmPurpose | null;
}

/** Pure props-driven table (exported test seam) — one select row per purpose. */
export function AssignmentTable({ models, assignments, onAssign, busyPurpose }: AssignmentTableProps) {
  return (
    <div className="divide-y rounded-md border">
      {assignmentPurposes.map((purpose) => (
        <div key={purpose} className="flex items-center justify-between gap-4 px-4 py-3">
          <code className="font-mono text-xs">{purpose}</code>
          <Select
            aria-label={`Model for ${purpose}`}
            className="w-72"
            value={assignments[purpose] ?? ''}
            disabled={busyPurpose === purpose || models.length === 0}
            onChange={(e) => onAssign(purpose, e.target.value)}
          >
            <option value="" disabled>
              {models.length === 0 ? 'No models registered' : 'Unassigned — pick a model'}
            </option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.internalName} ({m.provider}:{m.modelName})
              </option>
            ))}
          </Select>
        </div>
      ))}
    </div>
  );
}

export function Assignment() {
  const [models, setModels] = useState<LlmModel[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string | null>>({});
  const [busyPurpose, setBusyPurpose] = useState<LlmPurpose | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([hub.llmModelsList(), hub.llmAssignmentsList()])
      .then(([modelRows, assignmentRows]: [LlmModel[], LlmAssignmentView[]]) => {
        setModels(modelRows);
        setAssignments(Object.fromEntries(assignmentRows.map((a) => [a.purpose, a.modelId])));
      })
      .catch((err) => setError((err as Error).message));
  }, []);

  const onAssign = async (purpose: LlmPurpose, modelId: string) => {
    if (!modelId) return;
    setBusyPurpose(purpose);
    setError(null);
    try {
      await hub.llmAssignmentSet({ params: { purpose }, body: { modelId } });
      setAssignments((prev) => ({ ...prev, [purpose]: modelId }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyPurpose(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Model Assignment"
        description="Bind each hub purpose to a registered model. Unassigned purposes fall back to the default model."
        actions={[{ label: 'Manage models', to: '/admin/models', variant: 'outline' }]}
      />
      {error && <Alert variant="destructive">{error}</Alert>}
      {models.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            No models are registered yet. Add one under Models before assigning purposes.
          </CardContent>
        </Card>
      ) : (
        <AssignmentTable
          models={models}
          assignments={assignments}
          onAssign={(purpose, modelId) => void onAssign(purpose, modelId)}
          busyPurpose={busyPurpose}
        />
      )}
    </div>
  );
}
